import { AIConversation, BaseItem, ChatMessageItem, MessageItem } from "@aidkitorg/types/lib/convoai/conversation";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation } from "react-router-dom";
import { usePost } from "../API";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { useDebouncedEffect, useDebouncedCallback } from "../Hooks/Debounce";
import { ChatContext } from "./ChatContext";
import { WebRTCContext } from "./WebRTCContext";

export type ChatSessionContextType = {
  createdAt?: Date,
  enabled: boolean,
  setEnabled(enable: boolean): void,
  isLoading: boolean,
  loaded: boolean,
  termsAccepted?: string,
  setTermsAccepted(termsHash: string): void
}

export const conversationIdCookieName = "ai_coach_conversation_id";

export const ChatSessionContext = React.createContext<ChatSessionContextType>({
  enabled: false,
  isLoading: false,
  loaded: false,
  setEnabled() { console.warn('no chat session present, ignoring setEnabled()') },
  setTermsAccepted() { console.warn('no chat session present, ignoring setTermsAccepted()') },
});

export const ChatSessionProvider: React.FC<{ context: ChatSessionContextType, children: React.ReactNode }> = ({ context, children }) =>
  <ChatSessionContext.Provider value={context}>
    {children}
  </ChatSessionContext.Provider>;

/**
 * Put within any existing ChatContext, to enable persistence of the chat.
 */
export function DefaultChatSessionProvider({ children }: { children: React.ReactNode }) {
  const chat = useContext(ChatContext);
  const getOrCreateConversation = usePost('/coach/conversation/create', { suppressErrorDisplay: true });
  const updateConversation = usePost('/coach/conversation/update', { suppressErrorDisplay: true });
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [createdAt, setCreatedAt] = useState(new Date());
  const [termsAccepted, setTermsAccepted] = useState<string | undefined>();
  const [cookies, setCookie] = useCookies([conversationIdCookieName]);
  const id = cookies[conversationIdCookieName];
    
  // load chat messages
  useAsyncEffect(async (cancel) => {
    if (enabled && chat.active && !loaded) {
      const storedConvoId = cookies[conversationIdCookieName];
      setIsLoading(true);
      const existing = await getOrCreateConversation({ previousConvoId: storedConvoId ?? undefined }, cancel);
      if (existing && !("error" in existing)) {
        if (existing.conversationId) {
          setCookie(conversationIdCookieName, existing.conversationId)
        }
  
        setCreatedAt(new Date(existing.createdAt));
  
        const itemsToRestore = existing.items.map(item => item.message).filter(message => message.type !== "appState") as ChatMessageItem[];
        chat.restore(itemsToRestore);
  
        setLoaded(true);
      }
      else if ("error" in existing) {
        chat.handleAPIError?.((existing as any).error);
      }
      setIsLoading(false);
    }
  }, [id, enabled, chat.active, loaded, chat.sendMessage, getOrCreateConversation, chat.handleAPIError]);
  
  // update chat message
  useAsyncEffect(async (cancel) => {
    if (loaded && id && chat.latestUpdatedItem) {
      const updatedConversation = await updateConversation({
        id,
        messages: [chat.latestUpdatedItem]
      }, cancel);
      // it's possible for the conversationId to change, so reset it
      if (updatedConversation.conversationId) { 
        setCookie(conversationIdCookieName, updatedConversation.conversationId);
      }
      else if ("error" in updatedConversation) {
        chat.handleAPIError?.((updatedConversation as any).error);
      }
    }
  }, [loaded, id, chat.latestUpdatedItem, chat.handleAPIError]);

  return <ChatSessionProvider context={{
    enabled,
    setEnabled,
    createdAt,
    isLoading,
    loaded,
    termsAccepted,
    setTermsAccepted
  }}>
    {children}
  </ChatSessionProvider>
}
